import { actionBarSpacer, WbActionCommand } from '@workbuddy/ui/v2/components/action.bar';
import { ButtonAccent, ButtonDisplayStyle } from '@workbuddy/ui/v2/components/button';

// Schema `.actions` order does not control the bar. Convert listed before send would
// otherwise land in "others" between Download and Send.
const FINANCIAL_DOCUMENT_ACTION_IDS = ['download', 'send', 'email'] as const;

// Reissue is forward so an approved document leads with it; Void then sits in its split menu
// instead of being the highlighted action on the bar.
const FORWARD_ACTION_IDS = new Set(['saveAndSend', 'saveAsApproved', 'saveAsDraft', 'submitForApproval', 'approve', 'approveAndSend', 'accept', 'markSent', 'markAsSent', 'generateBill', 'convertToInvoice', 'retry', 'reissue']);
// Keep-open saves are workflow (they join the save split) but never forward, so they never become its primary.
const WORKFLOW_ACTION_IDS = new Set([...FORWARD_ACTION_IDS, 'saveAsDraftKeepOpen', 'saveKeepOpen', 'decline', 'cancel', 'revise', 'requestChanges', 'void', 'delete']);
const DOCUMENT_ACTION_IDS = new Set<string>(FINANCIAL_DOCUMENT_ACTION_IDS);
// Destructive status actions are never on the bar or in a status split: always the plain last items of the ⋯
// overflow. They keep their own confirm.
const DESTRUCTIVE_ACTION_IDS = new Set(['void', 'delete', 'cancel']);
// Payments sit in one control so actions whose availability differs never shift the bar: the payment action
// leads, the rest live in its menu. Ids stay individual commands; this only arranges them.
const PAYMENT_PRIMARY_ID = 'payment';
const PAYMENT_MENU_IDS = ['cardPayment', 'allocateCredit', 'sync'] as const;
const PAYMENT_ACTION_IDS = new Set<string>([PAYMENT_PRIMARY_ID, ...PAYMENT_MENU_IDS]);

/**
 * The payments control: the payment action as a split with the others in its menu; a "Payments" menu when two or
 * more of the others apply without it; a single one as a plain button (Sync alone goes to ⋯); nothing when none apply.
 */
function paymentsUnit(visible: readonly WbActionCommand[]): WbActionCommand | null {

  const primary = visible.find(command => command.id === PAYMENT_PRIMARY_ID);
  const menu = PAYMENT_MENU_IDS.flatMap(id => visible.filter(command => command.id === id));
  if (primary && menu.length) {
    return { id: 'payments', label: primary.label, iconLeft: primary.iconLeft, disabled: primary.disabled, tooltip: primary.tooltip, kind: 'split', pin: 'preferred', run: primary.run, items: menu };
  }
  if (primary) return primary;
  if (menu.length > 1) return { id: 'payments', label: 'Payments', iconLeft: 'fa-regular fa-hand-holding-dollar', kind: 'menu', pin: 'preferred', items: menu };
  // Never a one-item dropdown: a lone Sync is housekeeping and goes to ⋯; a lone card or credit action is a button.
  if (menu.length === 1) return menu[0].id === 'sync' ? { ...menu[0], pin: 'never' } : menu[0];
  return null;
}

/**
 * Bar order: document actions | record actions (Edit…) | lifecycle (save / Reissue split) | Payments | ⋯ (with
 * Void / Delete / Cancel as its plain last items).
 * One unit carries the brand emphasis: Payments while the document has something to settle (its payment action
 * is offered), otherwise the lifecycle unit as before. Drafts and Edit never offer payment, so they are unchanged.
 */
export function clusterFinancialDocumentActionCommands(visible: readonly WbActionCommand[]): WbActionCommand[] {

  const submits = visible.filter(command => WORKFLOW_ACTION_IDS.has(command.id));
  const documents = FINANCIAL_DOCUMENT_ACTION_IDS.flatMap(id => visible.filter(command => command.id === id));
  const payments = paymentsUnit(visible);
  const others = visible.filter(command => !WORKFLOW_ACTION_IDS.has(command.id) && !DOCUMENT_ACTION_IDS.has(command.id) && !PAYMENT_ACTION_IDS.has(command.id));
  const statuses = submits.filter(command => !DESTRUCTIVE_ACTION_IDS.has(command.id));
  const destructive = submits.filter(command => DESTRUCTIVE_ACTION_IDS.has(command.id));
  const lifecycle = lifecycleUnit(statuses);
  const settling = visible.some(command => command.id === PAYMENT_PRIMARY_ID);
  const emphasised = settling ? payments : lifecycle;

  const units: WbActionCommand[] = [];
  if (documents.length) {
    units.push(...documents);
    if (others.length || lifecycle || payments) {
      units.push(actionBarSpacer('document-cluster-gap'));
    }
  }
  units.push(...others);
  for (const unit of [lifecycle, payments]) {
    if (!unit) continue;
    units.push(unit === emphasised ? { ...unit, displayStyle: ButtonDisplayStyle.Secondary, accent: ButtonAccent.Accent, pin: 'preferred' } : unit);
  }
  units.push(...destructive.map(command => ({ ...command, pin: 'never' as const })));
  return units;
}

/**
 * Save / lifecycle actions (destructive ones excluded) as one split led by the forward step (Approve, Reissue…), or
 * that step as a plain button when nothing else would sit in its menu; null when none remain.
 */
function lifecycleUnit(statuses: readonly WbActionCommand[]): WbActionCommand | null {

  const primary = statuses.find(command => FORWARD_ACTION_IDS.has(command.id)) ?? statuses[0];
  if (!primary) return null;
  const rest = statuses.filter(command => command.id !== primary.id);
  if (!rest.length) return { ...primary, pin: 'preferred' };
  return {
    id: 'save-split',
    label: primary.label,
    iconLeft: primary.iconLeft,
    disabled: primary.disabled,
    tooltip: primary.tooltip,
    kind: 'split',
    pin: 'preferred',
    run: primary.run,
    items: rest,
  };
}
